/*  MUI  */
import {
    Icon,
    IconWithVariant,
    Modal,
    ModalBody,
    ModalFooter
} from '@auviknetworks/iceberg-ui';
import {
    Box,
    Button,
    Chip,
    Grid,
    Paper,
    TableCell,
    TextField,
    Typography
} from '@mui/material';

/*  Shared  */
import { KebabMenu } from 'application/Shared/Components/Menu/KebabMenu';
import { ManagedTable } from 'application/Shared/Components/Table';
import { TooltipQ } from 'application/Shared/Components/Tooltips';
import { FontSizes } from 'application/Shared/Constants';
import {
    Helpers,
    Notify,
    Response,
    Transition
} from 'application/Shared/Helpers';
import { Requests } from 'application/Shared/Requests';
import { debug } from 'application/Shared/Utils';

/*  axios  */
import axios from 'axios';
import i18n from 'i18n';
import { debounce } from 'lodash';

/*  React  */
import React, { Component } from 'react';
import { withTranslation } from 'react-i18next';

const { getData } = Response;

export const removeBasename = (displayName) =>
    // turns /Company/A/B/C to /A/B/C
    `/${displayName.split('/').slice(2).join('/')}`;

const headersUsersPreview = [
    {
        align: 'left',
        field: 'group_name',
        renderHeader: <strong>{i18n.t('Common.name')}</strong>,
        width: '40%'
    },
    {
        align: 'left',
        field: 'group_date',
        renderHeader: <strong>{i18n.t('Common.dateAdded')}</strong>,
        width: '20%'
    },
    {
        align: 'left',
        field: 'group_existing',
        renderHeader: <strong>{i18n.t('Common.existing_user')}</strong>,
        width: '1%'
    }
];

const searchFields = ['displayName', 'description', 'id'];
const defaultRowsPerPage = 10;

class ProviderGroups extends Component {
    constructor(props) {
        super(props);

        this.state = {
            cachedData: {},
            currentPage: 0,
            loading: true,
            nextPageToken: null,
            pageTokens: {
                0: null
            },
            rowsPerPage: defaultRowsPerPage,
            tenantGroups: [],
            tenantPreview: {
                open: false,
                promptTitle: '',
                users: []
            },
            userSearch: ''
        };

        /* requests */
        this.requests = new Requests();

        // Create debounced search handler
        this.debouncedSetSearch = debounce((value) => {
            this.setState({ userSearch: value });
        }, 300);
    }

    componentDidMount() {
        this.load();
    }

    componentDidUpdate(prevProps, prevState) {
        if (
            prevState.userSearch !== this.state.userSearch &&
            this.state.tenantPreview.open
        ) {
            this.previewGroup();
        }
    }

    componentWillUnmount() {
        this.unload();
        // Cancel any pending debounced calls
        this.debouncedSetSearch.cancel();
    }

    getHeaders = () => {
        const isGoogleProvider = this.props.providerRoute === 'google';
        const headers = [
            {
                align: 'left',
                field: 'displayName',
                renderHeader: <strong>{i18n.t('Common.group')}</strong>,
                sortable: true,
                sortableKey: 'displayName',
                width: isGoogleProvider ? '20%' : '30%'
            },
            {
                align: 'left',
                field: 'type',
                renderHeader: <strong>{i18n.t('Common.type')}</strong>,
                width: '20%'
            },
            {
                align: 'left',
                field: 'description',
                renderHeader: (
                    <strong>{i18n.t('Common.groupDescription')}</strong>
                ),
                width: isGoogleProvider ? '20%' : '30%'
            },
            {
                align: 'left',
                field: 'memberCount',
                renderHeader: <strong>{i18n.t('Common.total_users')}</strong>,
                sortable: true,
                sortableKey: 'memberCount',
                width: '15%'
            },
            {
                align: 'left',
                field: 'excluded',
                renderHeader: <strong>{i18n.t('Common.excluded')}</strong>,
                sortable: true,
                sortableKey: 'excluded',
                tooltip: 'provider.excludeGroup',
                width: '15%'
            },
            {
                align: 'right',
                field: null,
                renderHeader: <strong>{i18n.t('Common.actions')}</strong>,
                width: '10%'
            }
        ];
        if (!isGoogleProvider) {
            headers.splice(1, 1);
        }
        return headers;
    };

    load = async () => {
        const { clientId, providerRoute } = this.props;

        /* params */
        const params = { size: -1 };
        if (clientId) {
            params.platformTenantId = clientId;
            params.includeClients = true;
        }

        if (providerRoute === 'azure') {
            params.includeMemberCounts = true;
        }

        this.setState({ loading: true });

        let groupsResponse;
        let orgUnitsResponse;

        try {
            groupsResponse = await axios.get(`/${providerRoute}/groups`, {
                cancelToken: this.requests.token(),
                params
            });

            if (providerRoute === 'google') {
                orgUnitsResponse = await axios.get(
                    `/${providerRoute}/orgUnits`,
                    {
                        cancelToken: this.requests.token(),
                        params
                    }
                );
            }
        } catch (e) {
            debug.e('Error loading Groups', e);
        }

        let returnGroups = Helpers.isset(
            groupsResponse,
            'data',
            'data',
            'groups'
        )
            ? groupsResponse.data.data.groups
            : [];
        if (Helpers.isset(orgUnitsResponse, 'data', 'data', 'orgUnits', 0)) {
            const { orgUnits } = getData(orgUnitsResponse, { noId: true });
            returnGroups = returnGroups.concat(orgUnits);
        }

        this.setState({
            loading: false,
            tenantGroups: returnGroups
        });
    };

    unload = () => {
        this.requests.cancel();
    };

    saveGroup = async (id, exists, params) => {
        const { providerRoute } = this.props;

        try {
            if (exists) {
                await axios.put(`/${providerRoute}/tenantGroup/${id}`, params);
            } else {
                await axios.post(`/${providerRoute}/tenantGroup`, params);
            }

            this.load();
        } catch (err) {
            throw new Error(err?.reponse?.data?.message || 'An error occurred');
        }
    };

    enableGroup = async (row) => {
        const { exists, id, isOrgUnit } = row;
        const { clientId, t } = this.props;

        /* params */
        const params = { id, imported: true, isOrgUnit };
        if (clientId) {
            params.platformTenantId = clientId;
        }

        try {
            await this.saveGroup(id, exists, params);
            if (exists) {
                Notify.s(
                    t('Provider.MicrosoftConfiguration.success_group_updated')
                );
            } else {
                Notify.s(
                    t(
                        'Provider.MicrosoftConfiguration.success_group_enabled_added'
                    )
                );
            }
        } catch (err) {
            Notify.e(t('Provider.MicrosoftConfiguration.error_saving_group'));
        }
    };

    excludeGroup = async (row) => {
        const { excluded, exists, id, isOrgUnit } = row;
        const { clientId, t } = this.props;

        /* params */
        const params = { excluded: exists ? !excluded : true, id, isOrgUnit };
        if (clientId) {
            params.platformTenantId = clientId;
        }

        try {
            await this.saveGroup(id, exists, params);
            if (params.excluded) {
                Notify.s(
                    t('Provider.MicrosoftConfiguration.success_group_excluded')
                );
            } else {
                Notify.s(
                    t('Provider.MicrosoftConfiguration.success_group_included')
                );
            }
        } catch (err) {
            Notify.e(
                t('Provider.MicrosoftConfiguration.error_excluding_group')
            );
        }
    };

    openUsersModal = (row) => {
        const tenantPreviewBaseState = {
            groupData: row,
            open: true,
            promptTitle: row.displayName
        };
        this.setState({
            tenantPreview: {
                ...tenantPreviewBaseState,
                loading: true
            },
            userSearch: ''
        });
    };

    closeModal = () => {
        this.setState({
            rowsPerPage: defaultRowsPerPage,
            tenantPreview: {
                open: false
            },
            userSearch: ''
        });
    };

    previewGroup = async () => {
        const { clientId, providerRoute } = this.props;
        const { tenantPreview, userSearch, memberCount } = this.state;
        this.setState({
            tenantPreview: {
                ...tenantPreview,
                loading: true
            }
        });
        const row = tenantPreview.groupData;
        const { isOrgUnit, displayName } = row;
        const { currentPage, nextPageToken, pageTokens } = this.state;
        let rowId = row.id;
        const params = {};

        /* params */
        params.size = this.state.rowsPerPage;
        params.pageToken = nextPageToken;

        // Currently, only Azure/Entra supports search
        if (providerRoute === 'azure') {
            params.search = userSearch;
        }

        const cacheKey = `${rowId}_page${currentPage}_size${params.size}_search${userSearch}`;

        if (tenantPreview.open && this.state.cachedData[cacheKey]) {
            this.setState((prevState) => ({
                currentPage: prevState.currentPage + 1,
                pageTokens: prevState.cachedData[cacheKey].pageTokens,
                tenantPreview: {
                    ...prevState.tenantPreview,
                    count: prevState.cachedData[cacheKey].count ?? memberCount,
                    loading: false,
                    users: prevState.cachedData[cacheKey].users
                }
            }));
            return;
        }
        this.setState((prevState) => ({
            tenantPreview: {
                ...prevState.tenantPreview,
                loading: true
            }
        }));

        if (isOrgUnit) {
            params.isOrgUnit = isOrgUnit;
            // When searching, the base name (company name) is excluded
            // displayName usually includes the company name, which is not technically in the org unit path
            // ex. /Saaslio/Engineering -> /Engineering
            rowId = removeBasename(displayName).replaceAll('/', '%2F');
        }

        if (clientId) {
            params.platformTenantId = clientId;
            params.includeClients = true;
        }

        let users = [];
        let updatedPageTokens = pageTokens;
        let count;

        try {
            const response = await axios.get(
                `/${providerRoute}/tenantGroup/${rowId}`,
                {
                    cancelToken: this.requests.token(),
                    params
                }
            );

            const data = Response.getData(response, { noId: true });

            users = data?.users || [];

            if (providerRoute === 'azure') {
                // count in the response may be 0, which is a valid count, so we only need to fall back to the previous count if data.count is null/undefined.
                count = data?.count ?? tenantPreview?.count ?? 0;
            } else {
                // Google doesn't return a count, so we use the memberCount from the row
                count = memberCount;
            }

            updatedPageTokens = {
                ...updatedPageTokens,
                [currentPage + 1]: response?.data?.data?.nextPageToken || null
            };

            // Use cachedData if modal is still open
            if (this.state.tenantPreview.open) {
                this.setState((prevState) => ({
                    cachedData: {
                        ...prevState.cachedData,
                        [cacheKey]: {
                            count,
                            pageTokens: updatedPageTokens,
                            users
                        }
                    }
                }));
            }
        } catch (e) {
            debug.e('Error loading Group');
        }

        this.setState({
            currentPage: currentPage + 1,
            pageTokens: updatedPageTokens,
            tenantPreview: {
                ...tenantPreview,
                count,
                loading: false,
                users
            }
        });
    };

    handlePageChange = (page) => {
        const { pageTokens } = this.state;
        const nextPageToken = pageTokens[page];

        this.setState(
            {
                currentPage: page,
                nextPageToken
            },
            () => {
                this.previewGroup();
            }
        );
    };

    handleRowsPerPage = (rowsPerPage) => {
        this.setState(
            {
                currentPage: 0,
                nextPageToken: '',
                rowsPerPage
            },
            () => {
                this.previewGroup();
            }
        );
    };

    renderPreview = (row) => {
        const { t, providerRoute } = this.props;
        const { tenantPreview } = this.state;
        const loadingPreview = !!tenantPreview?.loading;
        const memberCount = tenantPreview?.count ?? row.memberCount ?? 0;

        return (
            <Modal
                open={tenantPreview.open}
                aria-labelledby="simple-dialog-title"
                TransitionComponent={Transition}
                title={tenantPreview.open && tenantPreview.promptTitle}
                data-testid="groupTablePreviewModal"
                onClose={this.closeModal}
                zIndexOverride={1000}
            >
                {tenantPreview.open && (
                    <>
                        <ModalBody sx={{ padding: '16px', width: '600px' }}>
                            {providerRoute === 'azure' && (
                                <Box marginBottom={3}>
                                    <TextField
                                        autoComplete="off"
                                        autoFocus
                                        fullWidth
                                        InputProps={{
                                            startAdornment: (
                                                <IconWithVariant
                                                    variant="search"
                                                    src="Search"
                                                    size={FontSizes[1]}
                                                    wrapperProps={{
                                                        marginRight: '4px'
                                                    }}
                                                />
                                            )
                                        }}
                                        onChange={(e) =>
                                            this.debouncedSetSearch(
                                                e.target.value
                                            )
                                        }
                                        placeholder={t(
                                            'ProviderGroups.searchByUserName'
                                        )}
                                        variant="outlined"
                                        size="small"
                                    />
                                </Box>
                            )}
                            <ManagedTable
                                disableFirstAndLastPageButtons
                                headers={headersUsersPreview}
                                externalLoading={loadingPreview}
                                handlePageChange={this.handlePageChange}
                                handleRowsPerPage={this.handleRowsPerPage}
                                renderBody={(row) => (
                                    <>
                                        <TableCell>
                                            {row.displayName}
                                            <br />
                                            <small>
                                                {row.serialId
                                                    ? row.tenantUserId
                                                    : row.id}
                                            </small>
                                        </TableCell>
                                        <TableCell>
                                            {row.dateCreated || '-'}
                                            <br />
                                        </TableCell>
                                        <TableCell align="left">
                                            <Icon
                                                src={
                                                    row.serialId
                                                        ? 'Check'
                                                        : 'Close'
                                                }
                                                size={FontSizes[2]}
                                            />
                                        </TableCell>
                                    </>
                                )}
                                renderBodyKey="id"
                                renderFooter
                                rows={tenantPreview.users}
                                rowCount={memberCount}
                                rowsPerPage={this.state.rowsPerPage}
                                sortDirection="ASC"
                                sortFieldType="string"
                                sortKey="displayName"
                            />
                        </ModalBody>
                        <ModalFooter>
                            <Button
                                onClick={this.closeModal}
                                data-testid="groupTableCloseButton"
                                data-trackid="admin_users_providerGroups_tenantPreview_close"
                                color="primary"
                                variant="outlined"
                            >
                                {t('Common.close')}
                            </Button>
                        </ModalFooter>
                    </>
                )}
            </Modal>
        );
    };

    renderTags = (row) => {
        const { t } = this.props;
        const { displayName, isOrgUnit } = row;
        return (
            <Box>
                {isOrgUnit ? (
                    <Chip
                        data-testid="groupTableChip"
                        label={
                            <Typography
                                fontSize={FontSizes[0]}
                                dangerouslySetInnerHTML={{
                                    __html: t('Common.org_unit_bold', {
                                        orgUnit: displayName
                                            .split('/')
                                            .slice(0, -1)
                                            .join('/')
                                    })
                                }}
                                sx={{ color: 'providerGroup.orgUnit.text' }}
                            />
                        }
                        size="small"
                        sx={{
                            backgroundColor:
                                'providerGroup.orgUnit.backgroundColor',
                            border: '1px solid',
                            borderColor: 'providerGroup.orgUnit.borderColor'
                        }}
                    />
                ) : (
                    <Chip
                        data-testid="groupTableChip"
                        label={
                            <Typography
                                fontSize={FontSizes[0]}
                                sx={{ color: 'providerGroup.default.text' }}
                            >
                                {t('Common.group')}
                            </Typography>
                        }
                        size="small"
                        sx={{
                            backgroundColor:
                                'providerGroup.default.backgroundColor',
                            border: '1px solid',
                            borderColor: 'providerGroup.default.borderColor'
                        }}
                    />
                )}
            </Box>
        );
    };

    renderRow = (row) => {
        const { t, providerRoute } = this.props;

        const options = [
            {
                hide: row?.imported,
                key: 'groupTableImportButton',
                label: t('Common.importReport'),
                value: 'groupImport'
            },
            {
                key: 'groupTablePreviewButton',
                label: t('Common.viewUsers'),
                value: 'viewUsers'
            },
            {
                key: 'groupTableExcludeButton',
                label: row?.excluded
                    ? t('Common.includeGroup')
                    : t('Common.excludeGroup'),
                tooltip: row?.excluded
                    ? t('Tooltips.provider.includeGroup')
                    : t('Tooltips.provider.excludeGroup'),
                value: 'excludeGroup'
            }
        ];

        const onClick = (value) => {
            if (row.loading) return;

            if (value === 'groupImport') {
                this.enableGroup(row);
            }
            if (value === 'viewUsers') {
                this.openUsersModal(row);
            }
            if (value === 'excludeGroup') {
                this.excludeGroup(row);
            }
        };

        return (
            <>
                <TableCell display="flex" data-testid="groupTableName">
                    {row.displayName.split('/').at(-1)}
                    <br />
                    <small>{row.id}</small>
                </TableCell>
                {providerRoute === 'google' && (
                    <TableCell>{this.renderTags(row)}</TableCell>
                )}
                <TableCell align="left">{row.description}</TableCell>
                <TableCell align="left">{row.memberCount ?? '-'}</TableCell>
                <TableCell>
                    {t(row.excluded ? 'Common.yes' : 'Common.no')}
                </TableCell>
                <TableCell align="right">
                    <KebabMenu
                        data-testid="groupTableMenuButton"
                        onClick={onClick}
                        options={options}
                    />
                </TableCell>
            </>
        );
    };

    render() {
        const { t, translationProvider } = this.props;
        const { loading, tenantGroups, tenantPreview } = this.state;

        return (
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <Box display="flex" alignItems="center">
                        <Typography variant="h3">
                            {t(`Provider.${translationProvider}.groups_title`)}
                        </Typography>
                        <TooltipQ
                            text={t(
                                `Provider.${translationProvider}.groups_title_tooltip`
                            )}
                            size={FontSizes[3]}
                        />
                    </Box>
                </Grid>
                <Grid item xs={12}>
                    <Paper
                        elevation={0}
                        variant="outlined"
                        data-testid="providerGroupInstructions"
                    >
                        {t(`Provider.${translationProvider}.Groups.sync_page`)}
                        <ManagedTable
                            dataTestId="providerGroupTable"
                            externalLoading={loading}
                            headers={this.getHeaders()}
                            renderBody={this.renderRow}
                            renderBodyKey="id"
                            renderFooter
                            rows={tenantGroups}
                            searchEnabled
                            searchFields={searchFields}
                            sortDirection="ASC"
                            sortFieldType="string"
                            sortKey="displayName"
                        />
                        {tenantPreview.open &&
                            this.renderPreview(tenantPreview.groupData)}
                    </Paper>
                </Grid>
            </Grid>
        );
    }
}

export default withTranslation()(ProviderGroups);
